import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  APP_SIDE_NAV_WIDTH,
  APP_SIDE_NAV_WIDTH_COLLAPSED,
  APP_SUB_NAV_WIDTH_COLLAPSED,
  NAV_ITEM_GROUPS,
} from "@miplatform/constants";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { Box, Collapse, Divider, Drawer } from "@mui/material";
import { SideNavGroupHeader } from "./SideNavGroupHeader";
import { SideNavItem } from "./SideNavItem";

/**
 * A collapsible side navigation drawer component that displays grouped navigation items.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string|null} props.selectedModule - The currently selected module, controls drawer visibility
 * @param {Object} props.groupedNavItems - Navigation items organized by groups
 * @param {Function} props.setSelectedModule - Function to update the selected module
 * @param {boolean} props.isMainNavCollapsed - Whether the main navigation is collapsed
 * @param {React.Ref} ref - Forwarded ref to the drawer component
 *
 * @returns {JSX.Element} A Material-UI Drawer containing grouped navigation items with collapsible sections
 */
export const SubNavDrawer = React.forwardRef(
  (
    {
      selectedModule,
      groupedNavItems,
      isMainNavCollapsed,
      setIsCollapsed,
      isSubNavCollapsed,
      setIsSubNavCollapsed,
      disableExpand,
    },
    ref
  ) => {
    const location = useLocation();

    const { classes, theme } = useStyles({ isMainNavCollapsed, disableExpand });

    const groupedNavItemKeys = Object.keys(groupedNavItems);
    const [openGroups, setOpenGroups] = useState({});

    useEffect(() => {
      // for some reason Object.keys doesn't play well with useState initial value; setting here
      setOpenGroups(
        groupedNavItemKeys.reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
    }, [selectedModule]);

    const handleGroupToggle = (groupName) => {
      setOpenGroups((prev) => ({
        ...prev,
        [groupName]: !prev[groupName],
      }));
    };

    const subNavDrawerWidth = isSubNavCollapsed
      ? APP_SUB_NAV_WIDTH_COLLAPSED
      : APP_SIDE_NAV_WIDTH;

    return (
      <Drawer
        ref={ref}
        open
        variant="permanent"
        hideBackdrop
        className={classes.subNavContainer}
        slotProps={{
          paper: {
            style: {
              position: "absolute",
              left: isMainNavCollapsed
                ? APP_SIDE_NAV_WIDTH_COLLAPSED
                : APP_SIDE_NAV_WIDTH,
              height: "100%",
              width: subNavDrawerWidth,
              zIndex: theme.zIndex.appBar,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowX: "hidden",
              boxShadow: "12px 0px 32px -24px rgba(0,0,0,0.33)",
              transition: "left 200ms ease-in-out",
              background: theme.palette.grey.light,
            },
          },
        }}
      >
        {!isSubNavCollapsed ? (
          <>
            <Box className={classes.navItemsContainer}>
              {groupedNavItemKeys?.map((groupName, index) => {
                const allPaths =
                  groupedNavItems[groupName]?.map((item) => item.path) || [];
                const matchingPaths = allPaths.filter((p) =>
                  location.pathname.startsWith(p)
                );
                const longestMatch = matchingPaths.reduce(
                  (longest, current) =>
                    current.length > longest.length ? current : longest,
                  ""
                );
                return (
                  <Box key={`${selectedModule}-group-${groupName}`}>
                    {groupName &&
                    groupName !== NAV_ITEM_GROUPS.NO_GROUP_NAME ? (
                      <SideNavGroupHeader
                        groupName={groupName}
                        isMainNavCollapsed={false}
                        isGroupCollapsed={!openGroups[groupName]}
                        toggleCollapse={() => handleGroupToggle(groupName)}
                      />
                    ) : (
                      index !== 0 && <Divider className={classes.divider} />
                    )}
                    <Collapse
                      in={
                        groupName && Object.keys(openGroups).length > 0
                          ? openGroups[groupName]
                          : true
                      }
                      appear={false}
                      slotProps={{
                        transition: {
                          appear: false,
                        },
                      }}
                    >
                      {groupedNavItems[groupName]?.map(
                        (
                          { name, path, end = false, items, testId, count },
                          index
                        ) => {
                          return (
                            <SideNavItem
                              key={`${path}-${index}`}
                              name={name}
                              path={path}
                              end={end}
                              testId={testId}
                              items={items}
                              isSubNavItem
                              isActive={path === longestMatch}
                              count={count}
                              onClick={() => setIsCollapsed(true)}
                            />
                          );
                        }
                      )}
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
            <Box className={classes.footer}>
              <Box
                className={classes.collapseSection}
                paddingRight={!isMainNavCollapsed ? 0 : 2}
              >
                <MiButton
                  data-testid="collapse-and-expand-icon"
                  className={classes.footerIcon}
                  startIcon={{
                    path: mdiChevronLeft,
                    iconColor: theme.palette.grey.medium,
                    iconSize: 1,
                  }}
                  onClick={() => setIsSubNavCollapsed(true)}
                  disableRipple
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box
            className={classes.collapsedContainer}
            onClick={() => {
              if (!disableExpand) {
                setIsSubNavCollapsed(false);
              }
            }}
          >
            <MiButton
              data-testid="collapse-and-expand-icon"
              className={classes.collapsedIcon}
              startIcon={{
                path: mdiChevronRight,
                iconColor: disableExpand
                  ? theme.palette.grey.darkAccent
                  : theme.palette.grey.medium,
                iconSize: 1,
              }}
              disableRipple
            />
          </Box>
        )}
      </Drawer>
    );
  }
);

const useStyles = makeStyles()(
  (theme, { isMainNavCollapsed, disableExpand }) => ({
    navItemsContainer: {
      overflowY: "auto",
      overflowX: "hidden",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      overflow: "hidden",
      minHeight: 40,
    },
    collapseSection: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: isMainNavCollapsed ? "center" : "flex-end",
    },
    subNavContainer: {
      maxWidth: "fit-content",
      height: "100%",
    },
    collapsedContainer: {
      height: "100%",
      cursor: !disableExpand ? "pointer" : "default",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      transition: "background 100ms ease-in-out",
      "&:hover": {
        background: !disableExpand
          ? theme.palette.grey.mediumLight
          : theme.palette.grey.light,
      },
    },
    divider: {
      margin: theme.spacing(2, 4),
    },
    footerIcon: {
      padding: isMainNavCollapsed
        ? theme.spacing(2)
        : theme.spacing(0, 4, 2, 0),
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    collapsedIcon: {
      cursor: !disableExpand ? "pointer" : "default",
      padding: theme.spacing(2),
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  })
);
